import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Collapses the collection guide text to a preview height and toggles the rest.
 * Without JavaScript (or when the text is short) the full text stays visible.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} content
 * @property {HTMLButtonElement} toggle
 * @property {HTMLElement} toggleLabel
 *
 * @extends {Component<Refs>}
 */
class CollectionSeo extends Component {
  requiredRefs = ['content', 'toggle', 'toggleLabel'];

  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  #setup() {
    this.#controller?.abort();
    this.#controller = new AbortController();

    const { content, toggle } = this.refs;
    const collapsedHeight = parseFloat(getComputedStyle(this).getPropertyValue('--collection-seo-collapsed')) || 260;

    // Only collapse when there is a meaningful amount of hidden text
    if (content.scrollHeight <= collapsedHeight + 80) {
      this.#setCollapsed(false);
      toggle.hidden = true;
      return;
    }

    toggle.hidden = false;
    this.#setCollapsed(true);

    // Keyboard users tabbing into a clipped link get the full text
    content.addEventListener(
      'focusin',
      () => {
        if (this.hasAttribute('data-collapsed')) this.#setCollapsed(false);
      },
      { signal: this.#controller.signal }
    );
  }

  /**
   * Toggles the collapsed state.
   */
  toggle() {
    const collapse = !this.hasAttribute('data-collapsed');
    this.#setCollapsed(collapse);

    // After collapsing a long text, bring the section start back into view
    if (collapse && this.getBoundingClientRect().top < 0) {
      this.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }
  }

  /**
   * @param {boolean} collapsed
   */
  #setCollapsed(collapsed) {
    const { toggle, toggleLabel } = this.refs;

    this.toggleAttribute('data-collapsed', collapsed);
    toggle.setAttribute('aria-expanded', String(!collapsed));
    toggleLabel.textContent = (collapsed ? this.dataset.labelMore : this.dataset.labelLess) ?? '';
  }
}

if (!customElements.get('collection-seo-component')) {
  customElements.define('collection-seo-component', CollectionSeo);
}
